/**
 * useEntitlements - Slice 2.4 (2026-05-20).
 *
 * Hook that fetches the current tenant's entitlements from the backend
 * and exposes a `hasModule(name)` helper. Used by SideNav to filter
 * NAV groups based on what the tenant actually bought.
 *
 * Per docs/architecture/15 §22 - entitlements separate from feature flags.
 * The shell's nav graph is the OneTrust-pattern rendering surface: a
 * DSPM-only tenant sees no Activity/Detection items even if their
 * feature flags are flipped on.
 */

import { useCallback, useEffect, useState } from 'react';

import { apiFetch, getAuthToken } from './api';

export interface Entitlements {
  sku: string;
  modules: string[];
  billingStatus: 'active' | 'trial' | 'demo' | 'suspended' | 'cancelled';
  trialModules: string[];
  trialEndsAt: string | null;
  /**
   * Tenant identity for the shell TopBar (Phase 0 item 1, 2026-05-26).
   * Lets the TopBar render the real tenant name + tier instead of the
   * previously-hardcoded "Acme Bank Ltd · Sovereign · BFSI". Null fields
   * when the tenant row is incomplete (defensive - should exist for any
   * provisioned tenant).
   */
  tenant?: {
    id: string;
    slug: string | null;
    displayName: string | null;
    tier: string | null;
    industry: string | null;
  };
}

interface UseEntitlementsResult {
  loading: boolean;
  error: string | null;
  entitlements: Entitlements | null;
  hasModule: (module: string) => boolean;
  hasAnyModule: (modules: ReadonlyArray<string>) => boolean;
  refresh: () => Promise<void>;
}

interface EntitlementsResponse {
  ok: boolean;
  data: Entitlements;
}

// Cache across hook instances (component remount shouldn't re-fetch).
let _cache: Entitlements | null = null;
let _cacheAt = 0;
const CACHE_TTL_MS = 60_000;

export function useEntitlements(): UseEntitlementsResult {
  const [state, setState] = useState<{
    loading: boolean;
    error: string | null;
    data: Entitlements | null;
  }>(() => ({
    loading: !_cache,
    error: null,
    data: _cache,
  }));

  const refresh = useCallback(async () => {
    if (!getAuthToken()) {
      // Not logged in - return null entitlements; surfaces stay hidden.
      setState({ loading: false, error: null, data: null });
      return;
    }
    try {
      const res = await apiFetch<EntitlementsResponse>('/v1/entitlements/current');
      _cache = res.data;
      _cacheAt = Date.now();
      setState({ loading: false, error: null, data: res.data });
    } catch (err) {
      const msg = (err as Error).message ?? 'Failed to load entitlements';
      // Don't blow up the shell - fall back to showing everything if
      // entitlements can't be loaded (defence-in-depth: server-side
      // route gates still enforce real entitlement).
      setState({ loading: false, error: msg, data: null });
    }
  }, []);

  useEffect(() => {
    if (_cache && Date.now() - _cacheAt < CACHE_TTL_MS) {
      setState({ loading: false, error: null, data: _cache });
      return;
    }
    void refresh();
  }, [refresh]);

  const hasModule = useCallback(
    (module: string): boolean => {
      if (!state.data) {
        // Loading or error - be permissive in UI; server enforces
        // truth via route gates.
        return true;
      }
      if (state.data.billingStatus === 'cancelled') return false;
      if (state.data.modules.includes(module)) return true;
      if (
        state.data.trialModules.includes(module) &&
        state.data.trialEndsAt &&
        new Date(state.data.trialEndsAt) > new Date()
      ) {
        return true;
      }
      return false;
    },
    [state.data],
  );

  const hasAnyModule = useCallback(
    (modules: ReadonlyArray<string>): boolean => {
      return modules.some((m) => hasModule(m));
    },
    [hasModule],
  );

  return {
    loading: state.loading,
    error: state.error,
    entitlements: state.data,
    hasModule,
    hasAnyModule,
    refresh,
  };
}
